import { useState, useRef } from "react";
import { Button, Select } from "antd";
import css from "./ClientBuilderPanel.module.css";
import { siteConfigService } from "../../../services/builder-services/builder.service";
import { toast } from "react-toastify";

// Generic editor for any `type: "imagePicker"` schema field — renders BOTH
// the existing library <Select> AND an upload button + live thumbnail +
// remove button, matching the admin logo editor's UI exactly (Upload →
// hidden <input type="file"> → FileReader.readAsDataURL() → Remove button
// once a custom upload is set). `source` is the companion "<key>Source"
// field's current value ("library" | "upload"); switching sources doesn't
// clear the other source's stored value, so toggling back and forth doesn't
// lose a previous library pick or upload.
function ImagePickerField({ label, options, source, value, onSourceChange, onValueChange, libraryPreviewPaths }) {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const isUpload = source === "upload";

    // Uploads to the same GCS-backed bucket every other admin upload
    // (dishes, pack categories, ...) uses — stores a real, permanent public
    // URL, never a base64 data: URI embedded in the site_configs JSON blob.
    const handleUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = "";
        setUploading(true);
        siteConfigService.uploadImage(file)
            .then(({ url }) => {
                onSourceChange("upload");
                onValueChange(url);
            })
            .catch(err => toast.error(err.message || "Failed to upload image"))
            .finally(() => setUploading(false));
    };

    const libraryPreviewSrc = !isUpload ? libraryPreviewPaths?.[value] : null;
    const thumbSrc = isUpload ? value : libraryPreviewSrc;

    return (
        <div style={{ padding: "8px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
            <span className={css.propLabel} style={{ display: "block" }}>{label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {thumbSrc && (
                    <img src={thumbSrc} alt={label} style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: "1px solid #e5e7eb" }} />
                )}
                <Button size="small" loading={uploading} onClick={() => fileInputRef.current?.click()}>Upload</Button>
                {isUpload && (
                    <Button size="small" danger disabled={uploading} onClick={() => { onSourceChange("library"); onValueChange(options[0]?.value); }}>
                        Remove
                    </Button>
                )}
            </div>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleUpload}
            />
            <Select
                value={!isUpload ? value : undefined}
                onChange={(v) => { onSourceChange("library"); onValueChange(v); }}
                options={options}
                size="small"
                placeholder="Pick from library"
                style={{ width: 220 }}
            />
        </div>
    );
}

export { ImagePickerField };
